import { type Dispatch } from 'react';

import { usePlaygroundOrchestrationReset } from '../../../hooks/usePlayground';
import { type AiResponse } from '../../../types/ai';
import { type ConversationAction } from '../conversation-reducer';

export const useRoutingReset = ({
  sessionId,
  dispatch,
  setSessionInfo,
  setSessionTransitionMessage,
  setErrorMessage,
}: {
  sessionId: string;
  dispatch: Dispatch<ConversationAction>;
  setSessionInfo: (session: AiResponse['session'] | null) => void;
  setSessionTransitionMessage: (message: string | null) => void;
  setErrorMessage: (message: string | null) => void;
}) => {
  const { mutateAsync: resetOrchestration, isPending } = usePlaygroundOrchestrationReset();

  const resetRouting = async () => {
    if (!sessionId || isPending) {
      return;
    }

    try {
      const response = await resetOrchestration({ sessionId, customerIdHash: null });
      setSessionInfo(response.session ?? null);
      setSessionTransitionMessage(
        response.resetFlags.length > 0
          ? `Routingstate återställdes: ${response.resetFlags.join(', ')}. Verifiering och chatt historik behölls.`
          : 'Routingstate återställdes. Verifiering och chatt historik behölls.',
      );
      dispatch({
        type: 'note/appended',
        message: {
          id: `routing-reset-${Date.now()}`,
          role: 'assistant',
          content:
            'Nödstopp aktiverat. Routing och orkestreringsstate är nu återställda till toppläget, men verifiering och tidigare chatt ligger kvar.',
          createdAt: new Date().toISOString(),
          status: 'success',
          routing: {
            reset: true,
            resetFlags: response.resetFlags,
          },
        },
      });
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'Kunde inte återställa routingstate just nu.',
      );
    }
  };

  return { resetRouting, isResettingRouting: isPending };
};
